import { appConfig } from "./app.config";
import type { SeoJsonLdRoute, SeoRoute, StaticSeoRoute } from "@/types/seo";

export const seoLastModified = "2026-06-21";

export const homepageSeo = {
  path: "/",
  label: "Video Transcript Generator",
  title: "VideoTranscriptSaver | Free Video to Text Transcription",
  description:
    "Upload, record, or paste a video link. Get a transcript, captions, summary, notes, and downloads. No login required.",
  h1: "VideoTranscriptSaver",
  intro:
    "Upload a video and get the words out: transcript, captions, summary, notes, and files you can download.",
  robots: "index,follow" as const,
  priority: 1,
  changeFrequency: "weekly" as const,
  lastModified: seoLastModified,
};

export const seoRoutes: SeoRoute[] = [
  {
    path: "/video-to-text",
    label: "Video to text",
    title: "Free Video to Text Converter Online",
    description:
      "Turn a video you upload, record, or link into searchable text, captions, and downloads you can use right away.",
    h1: "Free video to text converter",
    intro:
      "Drop in a video and get the spoken words as editable text. Search it, copy it, or save it for later.",
    audience:
      "Made for creators, students, researchers, assistants, trainers, and teams who would rather read the video than scrub through it.",
    howItWorks: [
      "Upload a supported media file, record a clip, or paste a direct media URL.",
      "We transcribe the audio and break it into readable segments.",
      "Search, copy, summarize, or download the transcript and captions.",
    ],
    practicalTips: [
      "Use clean audio and a quiet recording environment when possible.",
      "Choose a language when you know it, or keep auto-detect for mixed sources.",
      "Review important quotes against the original recording before publishing.",
    ],
    related: [
      "/transcribe-video-online",
      "/video-caption-generator",
      "/video-summary-generator",
    ],
    robots: "index,follow",
    priority: 0.95,
    changeFrequency: "weekly",
    lastModified: seoLastModified,
  },
  {
    path: "/mp4-to-text",
    label: "MP4 to text",
    title: "Convert MP4 to Text Online Free",
    description:
      "Upload an MP4 and get transcript text, captions, AI notes, and downloads without installing extra software.",
    h1: "Convert MP4 to text",
    intro:
      "Have an MP4 from a lesson, demo, interview, or phone recording? Turn it into text you can search and share.",
    audience:
      "Best for anyone with an MP4 on their device who needs the words quickly.",
    howItWorks: [
      "Choose an MP4 file from your device.",
      "We transcribe the audio while you wait.",
      "Download the transcript as TXT, Markdown, PDF, VTT, or SRT.",
    ],
    practicalTips: [
      "Trim very long files before uploading to stay inside the free processing limit.",
      "Use the title field so downloaded files are easy to recognize later.",
      "Generate AI notes after the transcript appears, not before reviewing it.",
    ],
    related: [
      "/video-to-text",
      "/transcribe-mp4-online",
      "/video-transcript-exporter",
    ],
    robots: "index,follow",
    priority: 0.92,
    changeFrequency: "weekly",
    lastModified: seoLastModified,
  },
  {
    path: "/mov-to-text",
    label: "MOV to text",
    title: "Convert MOV Video to Text Online",
    description:
      "Convert MOV files from iPhone, QuickTime, or camera recordings into text, captions, and notes.",
    h1: "Convert MOV video to text",
    intro:
      "Upload a MOV and pull out the spoken words so the recording is easier to search, quote, summarize, or share.",
    audience:
      "Helpful for iPhone recordings, team updates, class clips, client videos, and QuickTime exports.",
    howItWorks: [
      "Upload the MOV file from your device.",
      "We transcribe the audio track.",
      "Copy the text or download captions and notes.",
    ],
    practicalTips: [
      "Rename the transcript title before generating if the MOV filename is generic.",
      "If the video has background noise, verify names and technical terms carefully.",
      "Use Markdown export for notes apps and PDF export for quick sharing.",
    ],
    related: ["/mp4-to-text", "/webm-to-text", "/video-notes-generator"],
    robots: "index,follow",
    priority: 0.86,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/webm-to-text",
    label: "WebM to text",
    title: "Convert WebM to Text and Captions",
    description:
      "Upload or record WebM video in the browser, then turn it into transcript text, captions, and notes.",
    h1: "Convert WebM to text",
    intro:
      "WebM is common for browser recordings. Record or upload one here and get a readable transcript.",
    audience:
      "Useful for browser-recorded demos, async updates, bug reports, tutorials, and quick camera notes.",
    howItWorks: [
      "Record in the browser or upload a WebM file.",
      "We turn the audio into timestamped text.",
      "Export the transcript or download VTT/SRT captions.",
    ],
    practicalTips: [
      "Allow both camera and microphone permissions when recording.",
      "Keep recordings focused so the transcript is easier to review.",
      "Download VTT when the transcript needs to become video captions.",
    ],
    related: [
      "/record-video-transcript",
      "/video-caption-generator",
      "/video-to-text",
    ],
    robots: "index,follow",
    priority: 0.86,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/video-transcript-generator",
    label: "Transcript generator",
    title: "Video Transcript Generator With Upload, Record, and Link",
    description:
      "Generate a video transcript from an upload, browser recording, or direct media link.",
    h1: "Video transcript generator",
    intro:
      "Bring your own video and get a transcript without waiting for captions to exist somewhere else.",
    audience:
      "Made for original recordings, lessons, meetings, demos, interviews, and creator work.",
    howItWorks: [
      "Choose Upload Video, Record Video, or From Link.",
      "Check the file or link, then start the transcript.",
      "Review the timestamped text in your browser.",
    ],
    practicalTips: [
      "Do not include private details you do not want processed by AI.",
      "Use transcript search to check names, dates, and decisions.",
      "Clear the result on shared devices when you are done.",
    ],
    related: [
      "/transcribe-video-online",
      "/video-transcript-exporter",
      "/free-video-transcription-no-signup",
    ],
    robots: "index,follow",
    priority: 0.9,
    changeFrequency: "weekly",
    lastModified: seoLastModified,
  },
  {
    path: "/transcribe-video-online",
    label: "Online transcription",
    title: "Transcribe Video Online Free",
    description:
      "Transcribe video online from a file upload, browser recording, or direct media URL. No login required.",
    h1: "Transcribe video online",
    intro:
      "When you need the words from a video, start here. No desktop editor, no setup. No login required.",
    audience:
      "Useful for one-off tasks, remote teams, teachers, learners, and creators working across devices.",
    howItWorks: [
      "Choose a file, recording, or direct media link.",
      "Wait while the transcript is created.",
      "Copy, search, summarize, or download the transcript.",
    ],
    practicalTips: [
      "Shorter clips process faster and are easier to proofread.",
      "Use a direct file URL only when it points to the actual media file.",
      "Avoid uploading content you do not have permission to process.",
    ],
    related: [
      "/video-to-text",
      "/video-transcript-generator",
      "/video-summary-generator",
    ],
    robots: "index,follow",
    priority: 0.9,
    changeFrequency: "weekly",
    lastModified: seoLastModified,
  },
  {
    path: "/tiktok-video-transcript",
    label: "TikTok transcript",
    title: "TikTok Video Transcript Tool Waitlist",
    description:
      "Join the premium waitlist for TikTok video link transcription. Upload, record, or use direct media URLs while TikTok link support is being prepared.",
    h1: "TikTok video transcript",
    intro:
      "TikTok link transcription is planned for premium access. For now, you can still transcribe uploaded clips, browser recordings, and direct media file URLs.",
    audience:
      "Built for creators, marketers, researchers, assistants, and students who need short-form videos turned into searchable text.",
    howItWorks: [
      "Paste a TikTok link in the tool to see the premium waitlist prompt.",
      "Join the waitlist for early access when TikTok link transcription opens.",
      "Use upload, record, or direct media file links for transcripts today.",
    ],
    practicalTips: [
      "Save or export clips you have permission to process when a platform link is blocked.",
      "Use shorter clips for faster transcription and easier proofing.",
      "Review creator names, captions, and quoted lines before publishing notes.",
    ],
    related: [
      "/instagram-reels-transcript",
      "/transcribe-video-online",
      "/video-transcript-generator",
    ],
    robots: "index,follow",
    priority: 0.84,
    changeFrequency: "weekly",
    lastModified: seoLastModified,
  },
  {
    path: "/instagram-reels-transcript",
    label: "Reels transcript",
    title: "Instagram Reels Transcript Tool Waitlist",
    description:
      "Join the premium waitlist for Instagram Reels link transcription. Upload, record, or use direct media URLs while Reels link support is being prepared.",
    h1: "Instagram Reels transcript",
    intro:
      "Instagram Reels link transcription is planned for premium access. Current tools still support uploaded clips, browser recordings, and direct media file URLs.",
    audience:
      "Useful for social media teams, creators, coaches, researchers, and anyone turning short-form video ideas into notes or captions.",
    howItWorks: [
      "Paste an Instagram Reel link in the tool to see the premium waitlist prompt.",
      "Join the waitlist for early access when Reels link transcription opens.",
      "Use upload, record, or direct media file links for transcripts today.",
    ],
    practicalTips: [
      "Use original clips or exported media when Instagram blocks public link access.",
      "Keep videos short enough for the free processing limit.",
      "Proofread hashtags, names, and quoted phrases before reusing the transcript.",
    ],
    related: [
      "/tiktok-video-transcript",
      "/transcribe-video-online",
      "/video-caption-generator",
    ],
    robots: "index,follow",
    priority: 0.84,
    changeFrequency: "weekly",
    lastModified: seoLastModified,
  },
  {
    path: "/transcribe-mp4-online",
    label: "Transcribe MP4",
    title: "Transcribe MP4 Online With No Signup",
    description:
      "Upload an MP4 and turn the speech into searchable text, AI notes, and caption files.",
    h1: "Transcribe MP4 online",
    intro: "Turn an MP4 into readable text without taking notes by hand.",
    audience:
      "Helpful for students, editors, marketers, instructors, and support teams working with recorded MP4 clips.",
    howItWorks: [
      "Upload the MP4 file from your computer or phone.",
      "We transcribe the speech into clean text.",
      "Export the transcript, captions, summary, or notes.",
    ],
    practicalTips: [
      "Compress or trim long recordings before uploading to the free tier.",
      "Use captions export for video editors and TXT export for plain archives.",
      "Proofread names and jargon before sending the transcript to others.",
    ],
    related: [
      "/mp4-to-text",
      "/video-transcript-downloader",
      "/video-caption-generator",
    ],
    robots: "index,follow",
    priority: 0.86,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/video-caption-generator",
    label: "Captions",
    title: "Video Caption Generator for VTT and SRT",
    description:
      "Make VTT and SRT caption files from videos you upload, record, or link.",
    h1: "Video caption generator",
    intro:
      "Create captions while you make the transcript, then use the files in editors, players, or learning platforms.",
    audience:
      "Useful for creators, educators, accessibility reviewers, training teams, and anyone preparing videos for publication.",
    howItWorks: [
      "Transcribe your video.",
      "We use the timestamps to build caption files.",
      "Download VTT or SRT when you are ready.",
    ],
    practicalTips: [
      "Review caption timing before publishing a final video.",
      "Use VTT for web players and SRT for broad editing compatibility.",
      "Correct speaker names and technical terms manually after generation.",
    ],
    related: ["/webm-to-text", "/video-transcript-exporter", "/video-to-text"],
    robots: "index,follow",
    priority: 0.84,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/video-summary-generator",
    label: "Summaries",
    title: "Video Summary Generator From Transcript",
    description:
      "Turn a video transcript into a summary, key takeaways, action items, notes, and chapters.",
    h1: "Video summary generator",
    intro:
      "After the transcript is ready, AI Notes can pull out the parts you need most: the summary, takeaways, tasks, and chapters.",
    audience:
      "Best for long recordings, lessons, demos, interviews, meetings, webinars, and review sessions.",
    howItWorks: [
      "Generate the video transcript first.",
      "Open the AI Notes tab.",
      "Create summary sections you can copy into your workspace.",
    ],
    practicalTips: [
      "Treat AI notes as a draft and verify details against the transcript.",
      "Use key takeaways for quick review and chapters for navigation.",
      "Only use AI notes with recordings you have permission to process.",
    ],
    related: [
      "/video-notes-generator",
      "/meeting-video-to-text",
      "/lecture-video-to-text",
    ],
    robots: "index,follow",
    priority: 0.84,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/video-notes-generator",
    label: "Notes",
    title: "Video Notes Generator From Uploaded Video",
    description:
      "Create video notes, takeaways, action items, and chapters from a finished transcript.",
    h1: "Video notes generator",
    intro:
      "Use AI Notes when you want the useful bits from a recording without rereading the whole transcript.",
    audience:
      "Helpful for students, assistants, creators, operators, founders, coaches, and trainers reviewing recorded material.",
    howItWorks: [
      "Transcribe your video.",
      "Use AI Notes to organize the transcript into useful sections.",
      "Copy the notes or download the transcript for deeper editing.",
    ],
    practicalTips: [
      "Use transcript search to validate every important note.",
      "Add your own decisions and context after copying AI notes.",
      "Use Markdown export when notes are going into a knowledge base.",
    ],
    related: [
      "/video-summary-generator",
      "/lecture-video-to-text",
      "/meeting-video-to-text",
    ],
    robots: "index,follow",
    priority: 0.8,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/video-transcript-downloader",
    label: "Downloader",
    title: "Video Transcript Downloader - TXT, Markdown, PDF",
    description:
      "Download video transcripts as TXT, Markdown, PDF, VTT, or SRT after you upload, record, or link media.",
    h1: "Download video transcripts",
    intro:
      "Keep a copy you can open anywhere: plain text for archives, Markdown for docs, PDF for sharing, captions for video tools.",
    audience:
      "Built for people who need a local copy after turning a video into text.",
    howItWorks: [
      "Generate a transcript from an upload, recording, or direct link.",
      "Review and search the result.",
      "Download the file format that matches your next step.",
    ],
    practicalTips: [
      "Use TXT for plain archives and Markdown for notes or docs.",
      "Use PDF for a readable snapshot and captions for video tools.",
      "Name the transcript before generating so exports are easy to find.",
    ],
    related: [
      "/video-transcript-exporter",
      "/mp4-to-text",
      "/video-caption-generator",
    ],
    robots: "index,follow",
    priority: 0.82,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/video-transcript-exporter",
    label: "Exporter",
    title: "Export Video Transcripts as Text, Markdown, PDF, VTT, or SRT",
    description:
      "Export video transcripts and captions as text, Markdown, PDF, VTT, or SRT for notes, editors, and publishing.",
    h1: "Video transcript exporter",
    intro:
      "Once the transcript is done, choose the format that fits your next step.",
    audience:
      "Useful for content teams, educators, documentation writers, accessibility work, and personal archives.",
    howItWorks: [
      "Generate a transcript from upload, recording, or link.",
      "Choose copy, TXT, Markdown, PDF, VTT, or SRT actions.",
      "Use the export in your notes, editor, player, or documentation.",
    ],
    practicalTips: [
      "Keep timestamps when you need to return to exact moments.",
      "Use Markdown when the transcript will become an outline.",
      "Use captions when text needs to sync with a video player.",
    ],
    related: [
      "/video-transcript-downloader",
      "/video-caption-generator",
      "/video-to-text",
    ],
    robots: "index,follow",
    priority: 0.8,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/meeting-video-to-text",
    label: "Meetings",
    title: "Meeting Video to Text Converter",
    description:
      "Turn meeting recordings into searchable text, summaries, action items, notes, and chapters.",
    h1: "Meeting video to text",
    intro:
      "Make meetings easier to review. Pull out decisions, blockers, and next steps from the recording.",
    audience:
      "Useful for founders, managers, assistants, agencies, support teams, and remote collaborators.",
    howItWorks: [
      "Upload or link the meeting recording.",
      "Generate transcript text from the audio track.",
      "Use AI Notes for summary and action item drafts.",
    ],
    practicalTips: [
      "Get consent before recording or processing meetings.",
      "Review AI action items before assigning work.",
      "Remove sensitive details from shared exports when needed.",
    ],
    related: [
      "/video-notes-generator",
      "/video-summary-generator",
      "/transcribe-video-online",
    ],
    robots: "index,follow",
    priority: 0.76,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/lecture-video-to-text",
    label: "Lectures",
    title: "Lecture Video to Text for Study Notes",
    description:
      "Turn lecture videos into searchable text, study notes, summaries, and chapters.",
    h1: "Lecture video to text",
    intro:
      "Use lecture transcripts to find definitions, revisit examples, and build study notes from spoken lessons.",
    audience:
      "Built for students, teachers, tutors, training teams, and lifelong learners working from recorded lessons.",
    howItWorks: [
      "Upload or link the lecture recording.",
      "Create a searchable transcript.",
      "Create AI notes after reviewing the transcript.",
    ],
    practicalTips: [
      "Search for terms, formulas, names, and timestamps after transcription.",
      "Use chapters to split long lectures into review blocks.",
      "Verify technical terminology before studying from generated notes.",
    ],
    related: [
      "/video-notes-generator",
      "/transcribe-video-online",
      "/mp4-to-text",
    ],
    robots: "index,follow",
    priority: 0.76,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/record-video-transcript",
    label: "Record",
    title: "Record Video and Generate a Transcript Online",
    description:
      "Record video in the browser, then create a transcript, captions, summary, notes, and downloads.",
    h1: "Record video and generate a transcript",
    intro:
      "Record a quick clip with your camera and mic, then turn it into text on the same page.",
    audience:
      "Useful for async updates, quick lectures, product demos, bug reports, coaching notes, and personal reminders.",
    howItWorks: [
      "Open the Record Video tab and allow camera and microphone access.",
      "Stop the recording when the clip is ready.",
      "Generate the transcript and export the result.",
    ],
    practicalTips: [
      "Record in short focused clips for better review.",
      "Use a microphone when possible for cleaner transcription.",
      "Use reset only when you want to clear the local recording and transcript state.",
    ],
    related: [
      "/webm-to-text",
      "/video-transcript-generator",
      "/free-video-transcription-no-signup",
    ],
    robots: "index,follow",
    priority: 0.74,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
  {
    path: "/free-video-transcription-no-signup",
    label: "No login",
    title: "Free Video Transcription With No Login Required",
    description:
      "Get free video transcription, downloads, and no paywall. No login required.",
    h1: "Free video transcription with no login",
    intro:
      "The tool opens first, so you can create a transcript without onboarding or a paywall. No login required.",
    audience:
      "Good for one-off tasks, classrooms, shared devices, privacy-conscious users, and fast research.",
    howItWorks: [
      "Choose the media input method on the homepage.",
      "Stay within the free processing limit.",
      "Copy or download the transcript when the result appears.",
    ],
    practicalTips: [
      "Avoid uploading recordings you do not have permission to process.",
      "We do not store raw media or transcript text.",
      "Clear the result when using a shared computer.",
    ],
    related: [
      "/video-transcript-generator",
      "/transcribe-video-online",
      "/video-transcript-downloader",
    ],
    robots: "index,follow",
    priority: 0.82,
    changeFrequency: "monthly",
    lastModified: seoLastModified,
  },
];

export const staticSeoRoutes: StaticSeoRoute[] = [
  {
    path: "/privacy",
    label: "Privacy",
    title: "Privacy Policy | VideoTranscriptSaver",
    description:
      "What VideoTranscriptSaver processes, what it does not store, and how AI transcription and notes work.",
    robots: "index,follow" as const,
    priority: 0.3,
    changeFrequency: "monthly" as const,
    lastModified: seoLastModified,
  },
  {
    path: "/terms",
    label: "Terms",
    title: "Terms of Use | VideoTranscriptSaver",
    description:
      "Terms for using the free transcript, caption, summary, notes, and export features.",
    robots: "index,follow" as const,
    priority: 0.3,
    changeFrequency: "monthly" as const,
    lastModified: seoLastModified,
  },
];

export function canonicalUrl(path: string): string {
  const cleanPath = path === "/" ? "" : path;
  return `${appConfig.siteUrl}${cleanPath}`;
}

export function findSeoRoute(path: string): SeoRoute | undefined {
  return seoRoutes.find((route) => route.path === path);
}

export function getPrerenderRoutes(): string[] {
  return [
    homepageSeo.path,
    ...seoRoutes.map((route) => route.path),
    ...staticSeoRoutes.map((route) => route.path),
  ];
}

export function generateSitemapXml(): string {
  const routes = [homepageSeo, ...seoRoutes, ...staticSeoRoutes];

  const urls = routes
    .map(
      (route) => `  <url>
    <loc>${canonicalUrl(route.path)}</loc>
    <lastmod>${route.lastModified}</lastmod>
    <changefreq>${route.changeFrequency}</changefreq>
    <priority>${route.priority}</priority>
  </url>`,
    )
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls}
</urlset>
`;
}

export function webpageJsonLd(route: SeoJsonLdRoute) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: route.title,
    description: route.description,
    url: canonicalUrl(route.path),
    isPartOf: {
      "@type": "WebSite",
      name: appConfig.name,
      url: appConfig.siteUrl,
      logo: canonicalUrl(appConfig.assets.mark),
    },
  };
}

export function toolJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: appConfig.name,
    applicationCategory: "UtilitiesApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    featureList: [
      "Video and audio upload transcription",
      "In-browser video recording",
      "Direct media link transcription",
      "Transcript search and copy",
      "TXT, Markdown, PDF, VTT, and SRT exports",
      "AI summaries, notes, action items, and chapters",
    ],
    image: canonicalUrl(appConfig.assets.logo),
    logo: canonicalUrl(appConfig.assets.mark),
    url: appConfig.siteUrl,
  };
}
